import React from 'react';
import { PropTypes } from 'prop-types';
import { useNavigate } from 'react-router-dom';
import RecipeCollectionsAddMealCard from './recipes_collections_add_meal_card';
import { targetDayNutritionFromMetricConstraints } from '../../services/nutrition';
import { DEFAULT_PROGRAMME_CALORIE_SPLITS } from '../../services/meal_types';
import {
  dayIndexForCalendarDate,
  dbWeekStartDateStr,
  plannerWeekWithMealAdded,
  plannerWeekWithMealReplaced,
  viewWeekStartDateStr,
} from '../../services/planner';
import {
  useCreateMealMutation,
  useDeleteMealMutation,
  useGetDbPlannerWeekQuery,
  useGetGRCRecipesByIDsQuery,
  useGetSharedProgrammeQuery,
  useUpdateDbPlannerWeekOptimisticallyMutation,
  useUpdateMealMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { currentProgrammeTargetCalories } from '../../services/end_user_space_memberships';
import {
  scaleMealIngredientsOperation,
  updateFoodBrainDerivedDataOnMeal,
} from '../../operations/meal_operations';
import {
  mealWithScaledIngredientsReplaced,
  newMealFromGrcRecipe,
  newMealWithDefaultFields,
} from '../../services/meals';
import { customerFoodDataIDOfSpace } from '../../services/spaces';
import {
  useCurrentProgrammeEnrollmentQuery,
  useGetCurrentSpaceMembershipQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS } from '../../services/end_user_published_resources';

const EndUserFindAlternativesGRCRecipeItem = ({
  grcRecipeID,
  calendarDay,
  mealType,
  mealIDToReplace,
  onClick,
}) => {
  const navigate = useNavigate();

  const { data: grcRecipes } = useGetGRCRecipesByIDsQuery([grcRecipeID]);

  const grcRecipe = (grcRecipes || {})[grcRecipeID];

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const { data: currentProgrammeEnrollment } =
    useCurrentProgrammeEnrollmentQuery();

  const { sharedProgrammeID } = currentProgrammeEnrollment || {};

  const { data: sharedProgramme } = useGetSharedProgrammeQuery(
    sharedProgrammeID,
    {
      skip: !sharedProgrammeID,
      refetchOnMountOrArgChange: SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS,
    },
  );

  const [updateDbPlannerWeek] = useUpdateDbPlannerWeekOptimisticallyMutation();

  const [createMeal] = useCreateMealMutation();

  const [updateMeal] = useUpdateMealMutation();

  const personalisedMealScaling =
    sharedProgramme?.personalisedMealScaling || false;

  const programmeCalorieSplits =
    sharedProgramme?.calorieSplits || DEFAULT_PROGRAMME_CALORIE_SPLITS;

  const calorieSplitFraction =
    mealType &&
    programmeCalorieSplits.find((split) => split.mealType === mealType)
      ?.fraction;

  const targetCalories = currentProgrammeTargetCalories(currentSpaceMembership);

  const { nutritionConstraints } = sharedProgramme || {};

  const targetDayNutrition = targetDayNutritionFromMetricConstraints(
    nutritionConstraints?.nutritionMetricConstraints || [],
    ['calories'],
    targetCalories,
  );

  const targetDayCalories =
    targetDayNutrition?.calories?.targetQuantity || targetCalories;

  const targetCaloriesForScaling =
    personalisedMealScaling &&
    targetDayCalories &&
    calorieSplitFraction &&
    targetDayCalories * calorieSplitFraction;

  const plannerViewWeekStartDay = 1; /* plannerViewWeekStartDayOrDefault(
    userProfile?.plannerViewWeekStartDay,
  ); */

  const viewWeekStartDate = viewWeekStartDateStr(
    calendarDay,
    plannerViewWeekStartDay,
  );

  const viewWeekDayIndex = dayIndexForCalendarDate(calendarDay);

  const plannerDbWeekStartDate = dbWeekStartDateStr(calendarDay);

  const { data: plannerWeek } = useGetDbPlannerWeekQuery(
    plannerDbWeekStartDate,
    {
      skip: !plannerDbWeekStartDate,
    },
  );

  const [deleteMeal] = useDeleteMealMutation();

  const onAdd = async () => {
    const dayIndex = dayIndexForCalendarDate(calendarDay);
    const customerFoodDataID = customerFoodDataIDOfSpace(
      currentSpaceMembership?.spaceID,
    );
    const currentHealthProGroup = null;
    let mealWithoutId = newMealFromGrcRecipe(grcRecipe);
    const originalCalories =
      mealWithoutId?.derivedNutrition?.totalNutritionPerServing?.calories ||
      (mealWithoutId?.recipes || [])[0]?.nutrition?.calories;
    const shouldScale =
      targetCaloriesForScaling &&
      originalCalories &&
      originalCalories > 0 &&
      targetCaloriesForScaling > 0;
    if (shouldScale) {
      const scaleFactor = targetCaloriesForScaling / originalCalories;
      console.log({ scaleFactor });
      const scaledIngredients = await scaleMealIngredientsOperation(
        mealWithoutId,
        scaleFactor,
        customerFoodDataID,
      );
      const { servings } = mealWithoutId.recipes[0];
      mealWithoutId = mealWithScaledIngredientsReplaced(
        mealWithoutId,
        servings,
        scaledIngredients,
        scaleFactor,
      );
    }
    const input = newMealWithDefaultFields(
      mealWithoutId,
      plannerWeek.id,
      currentSpaceMembership?.id,
      currentHealthProGroup,
    );

    const { data: meal } = await createMeal(input);
    if (mealIDToReplace) {
      await deleteMeal({ id: mealIDToReplace, parentID: plannerWeek.id });
    }
    const updatedPlannerWeek = mealIDToReplace
      ? plannerWeekWithMealReplaced(
          plannerWeek,
          mealIDToReplace,
          meal,
          dayIndex,
        )
      : plannerWeekWithMealAdded(plannerWeek, meal, dayIndex);

    await updateDbPlannerWeek(updatedPlannerWeek);

    await updateFoodBrainDerivedDataOnMeal(
      meal,
      customerFoodDataID,
      updateMeal,
    );
    navigate(`/planner/${viewWeekStartDate}#${viewWeekDayIndex}`);
  };

  if (!grcRecipe) {
    return null;
  }

  return (
    <RecipeCollectionsAddMealCard
      title={grcRecipe.recipe.title}
      imageUrl={grcRecipe.recipe.imageUrl}
      onAdd={onAdd}
      onClick={() => onClick(grcRecipeID)}
      addEnabled={!!plannerWeek}
    />
  );
};

EndUserFindAlternativesGRCRecipeItem.propTypes = {
  grcRecipeID: PropTypes.string.isRequired,
  calendarDay: PropTypes.string.isRequired,
  mealType: PropTypes.string,
  mealIDToReplace: PropTypes.string,
  onClick: PropTypes.func.isRequired,
};

EndUserFindAlternativesGRCRecipeItem.defaultProps = {
  mealType: null,
  mealIDToReplace: null,
};

export default EndUserFindAlternativesGRCRecipeItem;
